import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './NotificationDropdown.css';

/**
 * NotificationDropdown Component
 * 
 * Displays user notifications in a dropdown.
 * Shows unread count badge, supports marking as read, and navigation to related entities.
 */
const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'

  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch notification count
  const fetchCount = useCallback(async () => {
    try {
      const response = await api.get('/notifications/count');
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const params = { page: pageNum, page_size: 10 };
      if (filter === 'unread') {
        params.is_read = false;
      }
      const response = await api.get('/notifications', { params });

      const data = response.data;
      if (append) {
        setNotifications(prev => [...prev, ...data.notifications]);
      } else {
        setNotifications(data.notifications || []);
      }
      setUnreadCount(data.unread_count || 0);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Initial fetch of count
  useEffect(() => {
    fetchCount();

    // Poll for new notifications every 60 seconds
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Fetch notifications when dropdown opens or filter changes
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchNotifications(1);
    }
  }, [isOpen, filter, fetchNotifications]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  // Mark notification as read and navigate
  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await api.post(`/notifications/${notification.id}/read`);
        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate to related entity
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-read', { mark_all: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Load more notifications
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage, true);
  };

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    const icons = {
      // Project
      project_added: '📁',
      project_removed: '📤',
      project_role_changed: '🔄',

      // Ticket
      ticket_assigned: '📌',
      ticket_reassigned: '🔄',
      ticket_updated: '✏️',
      ticket_status_changed: '📊',
      ticket_closed: '✅',
      ticket_reopened: '🔓',
      ticket_created: '➕',
      ticket_deleted: '🗑️',
      ticket_priority_changed: '⚡',

      // Comment
      comment_added: '💬',
      comment_edited: '✍️',
      comment_mention: '@',

      // User
      user_role_changed: '👤',
      user_activated: '✓',
      user_deactivated: '✗',
      user_invitation_accepted: '🎉',
      user_password_changed: '🔐',
      user_profile_updated: '📝'
    };

    return icons[type] || '🔔';
  };

  // Get relative time
  const getRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="notification-dropdown-container" ref={dropdownRef}>
      <button
        className="notification-bell"
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        <svg
          className="bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown notification-dropdown-enter">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read"
                onClick={handleMarkAllRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-filters">
            <button
              className={`notification-filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`notification-filter-btn ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </button>
          </div>

          <div className="notification-list">
            {loading && notifications.length === 0 ? (
              <div className="notification-loading">
                <span className="notification-spinner"></span>
                Loading...
              </div>
            ) : notifications.length > 0 ? (
              <>
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <span className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-meta">
                        {notification.triggered_by_name && (
                          <span className="notification-actor">
                            {notification.triggered_by_name}
                          </span>
                        )}
                        <span className="notification-time">
                          {getRelativeTime(notification.created_at)}
                        </span>
                      </div>
                    </div>
                    {!notification.is_read && (
                      <span className="notification-unread-dot"></span>
                    )}
                  </div>
                ))}

                {hasMore && (
                  <button
                    className="load-more-btn"
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </>
            ) : (
              <div className="notification-empty">
                <span className="empty-icon">🔔</span>
                <p>{filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}</p>
                <span className="empty-hint">
                  {filter === 'unread'
                    ? "You're all caught up!"
                    : "You'll see updates about your tasks and projects here"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
